import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SectionReveal from "@/components/SectionReveal";

export const ADDRESS = {
  name: "Kore Wellbeing",
  street: "Rowan House, Beresford Road",
  locality: "Bowness-on-Windermere",
  region: "Cumbria",
  postcode: "LA23 2JG",
};

export const localBusinessJsonLd = ({ url, description, type = "HealthClub" }) => ({
  "@context": "https://schema.org",
  "@type": type,
  name: ADDRESS.name,
  url,
  description,
  image: "https://korewellbeing.com/gallery/hero-gym-wide.webp",
  email: "info@korewellbeing.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: ADDRESS.street,
    addressLocality: ADDRESS.locality,
    addressRegion: ADDRESS.region,
    postalCode: ADDRESS.postcode,
    addressCountry: "GB",
  },
  areaServed: ["Windermere", "Bowness-on-Windermere", "Cumbria"],
});

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function LandingHero({ testId, eyebrow, h1, sub, image, imageAlt, images, primary, secondary, interval = 5000 }) {
  const slides = images?.length ? images : [{ src: image, alt: imageAlt }];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2 || reducedMotion()) return;
    const t = setInterval(() => setI((v) => (v + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [slides.length, interval]);

  return (
    <section data-testid={testId} className="relative min-h-[88vh] flex items-end overflow-hidden">
      {slides.map((s, idx) => (
        <img key={s.src} src={s.src} alt={idx === i ? s.alt : ""} aria-hidden={idx !== i}
          loading={idx === 0 ? "eager" : "lazy"} fetchPriority={idx === 0 ? "high" : "auto"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${idx === i ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-kore-ink/85 via-kore-ink/40 to-kore-ink/10" />
      <div className="relative container-kore pb-24 md:pb-32 max-w-4xl w-full">
        <span className="text-[12px] uppercase tracking-[0.18em] font-semibold text-kore-light">{eyebrow}</span>
        <h1 className="display-1 text-white mt-5">{h1}</h1>
        <p className="mt-8 max-w-xl text-white/85 text-lg leading-relaxed">{sub}</p>
        <div className="mt-10 flex flex-wrap gap-4 items-center">
          {primary.to.startsWith("#") ? (
            <a href={primary.to} data-testid={`${testId}-cta-primary`} onClick={primary.onClick} className="btn-gold">{primary.label} <ArrowRight size={16} /></a>
          ) : (
            <Link to={primary.to} data-testid={`${testId}-cta-primary`} onClick={primary.onClick} className="btn-gold">{primary.label} <ArrowRight size={16} /></Link>
          )}
          {secondary && (
            <Link to={secondary.to} data-testid={`${testId}-cta-secondary`} onClick={secondary.onClick} className="text-[15px] font-semibold text-white border-b border-white/60 pb-1 hover:border-white transition min-h-[44px] inline-flex items-center">
              {secondary.label}
            </Link>
          )}
        </div>
        {slides.length > 1 && (
          <div data-testid={`${testId}-dots`} className="mt-10 flex gap-2" role="tablist" aria-label="Hero photos">
            {slides.map((s, idx) => (
              <button key={s.src} role="tab" aria-selected={idx === i} aria-label={`Photo ${idx + 1}`} data-testid={`${testId}-dot-${idx}`} onClick={() => setI(idx)}
                className={`h-2.5 rounded-full transition-all ${idx === i ? "w-8 bg-white" : "w-2.5 bg-white/45 hover:bg-white/70"}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function TestimonialBand({ testId, quotes, interval = 7000 }) {
  const [start, setStart] = useState(0);
  useEffect(() => {
    if (!quotes || quotes.length <= 2 || reducedMotion()) return;
    const t = setInterval(() => setStart((v) => (v + 2) % quotes.length), interval);
    return () => clearInterval(t);
  }, [quotes, interval]);
  if (!quotes?.length) return null;
  const shown = [quotes[start % quotes.length], quotes.length > 1 ? quotes[(start + 1) % quotes.length] : null].filter(Boolean);
  const pages = Math.ceil(quotes.length / 2);
  return (
    <SectionReveal as="section" data-testid={testId} className="bg-kore-stone py-24 md:py-32">
      <div className="container-kore">
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16" aria-live="polite">
          {shown.map((t) => (
            <div key={t.name + t.short_quote} data-testid={`${testId}-quote`} className="animate-fade-in-up">
              <div className="flex gap-1">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={13} className="text-kore-forest fill-kore-forest" />)}</div>
              <blockquote className="mt-6 font-quote text-2xl md:text-3xl text-kore-ink leading-snug">"{t.short_quote || t.quote}"</blockquote>
              <div className="mt-6 text-[15px] text-kore-ink/70"><span className="font-semibold text-kore-ink">{t.name}</span><span className="text-[13px] text-kore-ink/55 ml-3">{t.role}</span></div>
            </div>
          ))}
        </div>
        {pages > 1 && (
          <div className="mt-10 flex gap-2" role="tablist" aria-label="More reviews">
            {Array.from({ length: pages }).map((_, p) => (
              <button key={p} role="tab" aria-selected={Math.floor(start / 2) === p} aria-label={`Reviews ${p + 1}`} data-testid={`${testId}-page-${p}`} onClick={() => setStart(p * 2)}
                className={`h-2.5 rounded-full transition-all ${Math.floor(start / 2) === p ? "w-8 bg-kore-forest" : "w-2.5 bg-kore-forest/30 hover:bg-kore-forest/60"}`} />
            ))}
          </div>
        )}
      </div>
    </SectionReveal>
  );
}

export function FaqSection({ testId, h2, faqs }) {
  return (
    <SectionReveal as="section" data-testid={testId} className="bg-kore-warm py-24 md:py-32">
      <div className="container-kore max-w-3xl">
        <span className="eyebrow">Questions</span>
        <h2 className="display-2 mt-4 text-kore-forest">{h2}</h2>
        <Accordion type="single" collapsible className="mt-12">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`${testId}-item-${i}`}>
              <AccordionTrigger className="text-left font-display text-xl text-kore-forest py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="text-kore-ink/75 leading-relaxed text-base pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </SectionReveal>
  );
}

export function CrossSell({ testId, eyebrow, h2, body, to, label, onClick }) {
  return (
    <SectionReveal as="section" data-testid={testId} className="bg-kore-warm py-20 md:py-24 border-t border-kore-gold/15">
      <div className="container-kore grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="font-display text-3xl md:text-4xl text-kore-forest mt-4 leading-tight">{h2}</h2>
          <p className="mt-4 text-kore-ink/75 leading-relaxed max-w-xl">{body}</p>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Link to={to} data-testid={`${testId}-link`} onClick={onClick} className="btn-ghost">{label} <ArrowRight size={14} /></Link>
        </div>
      </div>
    </SectionReveal>
  );
}

export function FinalCta({ testId, h2, primary, secondary, note }) {
  return (
    <SectionReveal as="section" data-testid={testId} className="py-32 md:py-40 bg-kore-forest text-kore-warm text-center">
      <div className="container-kore max-w-3xl">
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.015em]">{h2}</h2>
        <div className="mt-14 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link to={primary.to} data-testid={`${testId}-primary`} onClick={primary.onClick} className="btn-gold">{primary.label} <ArrowRight size={16} /></Link>
          {secondary && <Link to={secondary.to} data-testid={`${testId}-secondary`} onClick={secondary.onClick} className="text-sm text-kore-warm/90 border-b border-kore-light pb-1 hover:text-kore-light transition">{secondary.label}</Link>}
        </div>
        {note && <p className="mt-12 text-[11px] uppercase tracking-[0.24em] text-kore-light/80">{note}</p>}
      </div>
    </SectionReveal>
  );
}
